import { Tooltip, TooltipContent, TooltipTrigger } from '@roger/ui-components';
import { useId } from 'react';
import {
  Ban,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
} from 'lucide-react';

import type { EnrollmentSupport } from '../../services/clinicsApi';
import { markLabel, type SummaryTone } from './payerEnrollments';

// A mark that needs enrollment shows where the clinics stand: a filled green
// tick once live, like the automatic one; a grey dashed circle while it is not
// live yet (not started); a grey clock once submitted; a red circled
// "!" when somebody has to act.

/**
 * A green disc with the tick cut out of it, so the tick shows whatever is
 * behind it — a row's hover or selected tint — rather than a white one.
 */
function FilledCheck({ className }: { className: string }) {
  const mask = useId();
  return (
    <svg viewBox='0 0 24 24' className={className} aria-hidden='true'>
      <mask id={mask}>
        <rect width='24' height='24' fill='white' />
        <path
          d='m8 12 3 3 5-6'
          fill='none'
          stroke='black'
          strokeWidth='2.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </mask>
      <circle
        cx='12'
        cy='12'
        r='10'
        fill='currentColor'
        mask={`url(#${mask})`}
      />
    </svg>
  );
}
// The filled disc stops short of its box, so at the same box size it looks
// smaller than an outlined circle. Outlined marks are drawn a size
// down so the circles read as the same size side by side.
// Box, filled mark, outlined mark (drawn smaller, see above), grey cross.
const SIZES = {
  default: { box: 'h-5 w-5', full: 'h-5 w-5', outlined: 'h-4 w-4' },
  medium: {
    box: 'h-[22px] w-[22px]',
    full: 'h-[22px] w-[22px]',
    outlined: 'h-[18px] w-[18px]',
  },
  large: { box: 'h-6 w-6', full: 'h-6 w-6', outlined: 'h-5 w-5' },
} as const;

interface SupportIconProps {
  support?: EnrollmentSupport;
  /**
   * Where the clinics in view stand on this transaction. Only read for an
   * outlined tick; undefined keeps Stedi's own green.
   */
  state?: SummaryTone | null;
  /** Large on the payer table's rows; default elsewhere. */
  size?: keyof typeof SIZES;
}

/**
 * Stedi's three marks, as its payer network table draws them: a filled tick,
 * an outlined tick when enrollment is required, and a grey circle-slash for
 * not supported — with the
 * outlined tick coloured by status, and filled in once every clinic is live.
 */
export function SupportIcon({
  support,
  state,
  size = 'default',
}: SupportIconProps) {
  const { box, full, outlined } = SIZES[size];
  const value = support ?? 'unknown';
  const required = value === 'enrollment_required';
  const tracked = required && state !== undefined;
  const label = markLabel(support, state);
  const icon =
    value === 'enrollment_not_required' || (tracked && state === 'enrolled') ? (
      <FilledCheck className={`${full} text-green-600`} />
    ) : tracked && state === 'rejected' ? (
      <CircleX className={`${outlined} text-red-600`} />
    ) : tracked && state === 'needs_action' ? (
      <CircleAlert className={`${outlined} text-red-600`} />
    ) : tracked && state === 'waiting' ? (
      <Clock className={`${outlined} text-muted-foreground`} />
    ) : tracked ? (
      <CircleDashed className={`${outlined} text-muted-foreground`} />
    ) : required ? (
      <CircleCheck className={`${full} text-green-600`} />
    ) : value === 'not_supported' ? (
      <Ban className={`${outlined} text-muted-foreground/60`} />
    ) : (
      <span className='text-muted-foreground'>—</span>
    );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex ${box} items-center justify-center`}
          aria-label={label}
          role='img'
        >
          {icon}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
